// @flow

import React from "react"
import { injectIntl } from "react-intl"
import { observer } from "mobx-react"
import { Timeline } from "antd"
import OrderSearchStore from "shared/stores/OrderSearchStore"
import { isUndefined, get, camelCase, startCase } from "lodash"
import WideSpinner from "shared/components/WideSpinner"
import styled from "styled-components"
import moment from "moment"
import { includes } from "lodash"
import OrderSearchUtils from "apps/orders/OrderSearchUtils"

const Title = styled.div`
  font-size: 18px;
  font-weight: 800;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #000000;
  margin-bottom: 20px;
`

const HighlightedFontRow = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  width: 100%,
  font-size: 15px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #000000;
`

const SecondaryFontRow = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  width: 100%,
  font-size: 12px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #808080;
`

const TotalPriceRow = styled(HighlightedFontRow)`
  margin-bottom: 10px;
`

const PaymentTypeRow = styled(SecondaryFontRow)`
  margin-top: 10px;
`

const Label = styled.div`
  display: flex;
  align-self: flex-start;
  flex-flow: column wrap;
  width: 65%;
`

const Value = styled.div`
  display: flex;
  flex-flow: raw wrap;
  justify-content: flex-end;
  width: 35%;
`

const DateTime = styled.div`
  font-size: 12px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #989898;
`

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background-color: #d8d8d8;
  margin-bottom: 10px;
`

const PaymentPanel = props => {
  const {
    intl: { formatMessage }
  } = props

  const { detailedOrder } = OrderSearchStore
  const useFulTransactions = [
    "INITIALIZED",
    "RESERVED",
    "SETTLED",
    "SETTLE_FAILED",
    "SETTLE_TERMINATED",
    "VOIDED",
    "UNTRACKED"
  ]

  const renderPaymentTypeInfo = () => {
    const paymentMethodType = get(detailedOrder, "payment.paymentMethod.type")
    return (
      <PaymentTypeRow>
        <Label>
          {paymentMethodType === "CREDIT_CARD" &&
            get(detailedOrder, "payment.paymentMethod.cardType", "-") + " "}
          {paymentMethodType === "CREDIT_CARD" &&
            get(detailedOrder, "payment.paymentMethod.last4", "-")}
          {paymentMethodType !== "CREDIT_CARD" &&
            startCase(camelCase(paymentMethodType))}
        </Label>
      </PaymentTypeRow>
    )
  }

  const renderAdjustmentsToBasePrice = () => {
    let productPrices = get(detailedOrder, "economyContent.productPrice")
    const product = get(detailedOrder, "product")
    const voucher = get(detailedOrder, "economyContent.voucher")
    const discount = get(detailedOrder, "economyContent.totalDiscount")
    const fees = get(detailedOrder, "economyContent.fees")
    let basePrice = get(detailedOrder, "economyContent.basePrice", "-")
    if (product) {
      if (product.optionIds)
        productPrices = productPrices.filter(p => {
          return product.optionIds.includes(p.id)
        })
      else {
        const subProd = productPrices.find(p => {
          return p.id === product.subProductId
        })
        const subProdPrice = get(subProd, "price.amount", 0)

        if (subProdPrice > 0)
          basePrice = {
            amount: basePrice.amount + subProdPrice,
            currency: basePrice.currency
          }
        productPrices = []
      }
    }
    return (
      <React.Fragment>
        <SecondaryFontRow>
          <Label>
            {formatMessage({ id: "apps.order-search.payment.trip-price" })}
          </Label>
          <Value>{OrderSearchUtils.priceObjToText(basePrice)}</Value>
        </SecondaryFontRow>
        {productPrices.map((item, index) => (
          <SecondaryFontRow key={index}>
            <Label>{formatMessage({ id: "orderAttributes." + item.id })}</Label>
            <Value>
              {OrderSearchUtils.priceObjToText(get(item, "price", "-"))}
            </Value>
          </SecondaryFontRow>
        ))}
        {fees.length > 0 &&
          fees.map(item => (
            <SecondaryFontRow key={item.id}>
              <Label>{formatMessage({ id: "order.fees." + item.id })}</Label>
              <Value>
                {OrderSearchUtils.priceObjToText(get(item, "amount", "-"))}
              </Value>
            </SecondaryFontRow>
          ))}
        {voucher && (
          <SecondaryFontRow>
            <Label>{formatMessage({ id: "apps.order-search.voucher" })}</Label>
            <Value>
              {"-" +
                OrderSearchUtils.priceObjToText(get(voucher, "amount", "-"))}
            </Value>
          </SecondaryFontRow>
        )}
        {get(discount, "amount") !== 0 && (
          <SecondaryFontRow>
            <Label>{formatMessage({ id: "apps.order-search.discount" })}</Label>
            <Value>{OrderSearchUtils.priceObjToText(discount)}</Value>
          </SecondaryFontRow>
        )}
      </React.Fragment>
    )
  }

  const renderTransactionDetails = () => {
    const transactions = get(detailedOrder, "payment.transactions").filter(t =>
      includes(useFulTransactions, get(t, "eventType"))
    )
    return (
      <React.Fragment>
        <Timeline>
          {transactions.map((item, index) => (
            <Timeline.Item key={index}>
              {formatMessage({
                id: "apps.order-search.payment.event." + get(item, "eventType")
              })}
              <DateTime>
                {moment(get(item, "date")).format("DD MMMM, hh:mm")}
              </DateTime>
            </Timeline.Item>
          ))}
        </Timeline>
        <Separator />
      </React.Fragment>
    )
  }

  const renderPaymentTimeline = () => {
    if (
      !isUndefined(detailedOrder) &&
      !isUndefined(detailedOrder.economyContent)
    ) {
      return (
        <div data-cy="cy_orderPaymentPanel">
          <Title>
            {formatMessage({ id: "apps.order-search.title.payments" })}
          </Title>
          {get(detailedOrder, "payment.transactions") &&
            renderTransactionDetails()}
          <TotalPriceRow>
            <Label>
              {formatMessage({ id: "apps.order-search.payment.total-price" })}
            </Label>
            <Value>
              {OrderSearchUtils.priceObjToText(
                get(detailedOrder, "economyContent.customerPrice", "-")
              )}
            </Value>
          </TotalPriceRow>
          {renderAdjustmentsToBasePrice()}
          {renderPaymentTypeInfo()}
        </div>
      )
    } else {
      return <WideSpinner />
    }
  }

  return <div>{renderPaymentTimeline()}</div>
}

export default (injectIntl(observer(PaymentPanel)): any)
